import React, { createContext, useCallback, useContext, useMemo, useReducer } from "react";
import { Metadata } from "../shared/models/metadata.model";
import { FileState } from "./state-models";

export enum FileActionName {
    UPDATE_PATH = "UPDATE_PATH",
    UPDATE_CONTENT = "UPDATE_CONTENT",
    UPDATE_BASELINE_CONTENT = "UPDATE_BASELINE_CONTENT",
    UPDATE_METADATA = "UPDATE_METADATA",
}

export type FileStateAction =
    | { type: FileActionName.UPDATE_PATH; payload: FileState["path"] }
    | { type: FileActionName.UPDATE_CONTENT; payload: FileState["content"] }
    | { type: FileActionName.UPDATE_BASELINE_CONTENT; payload: FileState["baselineContent"] }
    | { type: FileActionName.UPDATE_METADATA; payload: FileState["metadata"] };

export const fileStateReducer = (state: FileState, action: FileStateAction): FileState => {
    let newState = state;
    switch (action.type) {
        case FileActionName.UPDATE_PATH:
            newState = state.path === action.payload ? state : { ...state, path: action.payload };
            break;
        case FileActionName.UPDATE_CONTENT:
            newState = state.content === action.payload ? state : { ...state, content: action.payload };
            break;
        case FileActionName.UPDATE_BASELINE_CONTENT:
            newState = state.baselineContent === action.payload ? state : { ...state, baselineContent: action.payload };
            break;
        case FileActionName.UPDATE_METADATA:
            newState = state.metadata?._id?.str === action.payload?._id?.str ? state : { ...state, metadata: action.payload };
            break;
        default:
            break;
    }
    return newState;
};

type FileContextType = {
    fileState: FileState;
    actions: Record<FileActionName, (...args: any[]) => void>;
} | null;

export const FileStateContext = createContext<FileContextType | undefined>(undefined);

type FileStateProviderProps = {
    children: React.ReactNode;
    initialState?: FileState;
};

export const FileStateProvider = ({ children, initialState = {} }: FileStateProviderProps) => {
    const memoizedInitialState = useMemo(() => initialState, [initialState]);
    const [state, dispatch] = useReducer(fileStateReducer, memoizedInitialState);

    // Create actions for updating the state
    // These actions are used to update the state in the current state branch
    const actions: Record<FileActionName, (...args: any[]) => void> = useMemo(() => {
        return {
            [FileActionName.UPDATE_PATH]: (props: string) => dispatch({ type: FileActionName.UPDATE_PATH, payload: props }),
            [FileActionName.UPDATE_CONTENT]: (props: string) => dispatch({ type: FileActionName.UPDATE_CONTENT, payload: props }),
            [FileActionName.UPDATE_BASELINE_CONTENT]: (props: string) => dispatch({ type: FileActionName.UPDATE_BASELINE_CONTENT, payload: props }),
            [FileActionName.UPDATE_METADATA]: (props: Metadata) => dispatch({ type: FileActionName.UPDATE_METADATA, payload: props }),
        };
    }, []);

    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({ fileState: state, actions }), [state, actions]);

    return <FileStateContext.Provider value={contextValue}>{children}</FileStateContext.Provider>;
};

export const useFileContext = () => {
    const context = useContext(FileStateContext);
    if (!context) {
        throw new Error("useFileContext must be used within a FileStateProvider");
    }
    return context;
};

export const usePath = () => {
    const { fileState } = useFileContext();
    return fileState.path;
}

export const useContent = () => {
    const { fileState } = useFileContext();
    return fileState.content;
}

export const useBaselineContent = () => {
    const { fileState } = useFileContext();
    return fileState.baselineContent;
}

export const useMetadata = () => {
    const { fileState } = useFileContext();
    return fileState.metadata;
}

// Selector for state modifying actions
export const useFileAction = (actionName: FileActionName): ((...args: any[]) => void) => {
    const context = useContext(FileStateContext);
    if (!context) {
        throw new Error('useFileAction must be used within a FileStateProvider');
    }
    return useCallback((...args: any[]) => context.actions[actionName](...args), [context.actions, actionName]);
}
